import { createContext, createSignal, useContext, type JSX, createEffect, on } from 'solid-js'
import { type User } from '../../features/auth/domain/models/user'
import { userApiService } from '../../features/auth/data/user-api.service'
import type { MeApiResponse, OrganizationMembership } from '../../features/auth/domain/models/me-api'
import { AppRoutes } from '../app-routes'

export interface UserContextType {
  readonly currentUser: () => User | undefined
  readonly setCurrentUser: (user: User) => void
  readonly hasAccess: () => boolean
  readonly organizationSlug: () => string | undefined
  readonly currentOrganization: () => OrganizationMembership | undefined
  readonly setCurrentOrganization: (org: OrganizationMembership) => void
  readonly organizationMemberships: () => OrganizationMembership[]
  readonly meData: () => MeApiResponse | undefined
  readonly isLoadingMe: () => boolean
}

const [currentUser, setCurrentUser] = createSignal<User>()
const [meData, setMeData] = createSignal<MeApiResponse>()
const [currentOrganization, setCurrentOrganization] = createSignal<OrganizationMembership | undefined>(undefined, {
  equals: (a, b) => a?.organization_id === b?.organization_id
})
const [isLoadingMe, setIsLoadingMe] = createSignal(false)
const [hasFetchedMe, setHasFetchedMe] = createSignal(false)

const SELECTED_ORG_ID_KEY = 'selected_organization_id'

createEffect(
  on(currentUser, async (user) => {
    if (!user || hasFetchedMe()) return

    setIsLoadingMe(true)
    try {
      const data = await userApiService.getMe()
      setMeData(data)

      const memberships = data.organization_memberships ?? []
      if (memberships.length === 0) {
        // Sign out and stay on the login page if the user isn't authorized.
        setHasFetchedMe(true)
        const loginUrl = new URL(window.location.origin + AppRoutes.Login())
        loginUrl.searchParams.set('error', 'insufficient_permissions')
        await window.Clerk?.signOut({ redirectUrl: loginUrl.toString() })
        return
      }

      const savedOrgId = localStorage.getItem(SELECTED_ORG_ID_KEY)
      const savedOrg = memberships.find((org) => org.organization_id === savedOrgId)

      setCurrentOrganization(savedOrg ?? memberships[0])

      setHasFetchedMe(true)
    } catch (error) {
      console.error('Failed to fetch /me:', error)
    } finally {
      setIsLoadingMe(false)
    }
  })
)

createEffect(() => {
  const org = currentOrganization()
  if (org) {
    localStorage.setItem(SELECTED_ORG_ID_KEY, org.organization_id)
  }
})

const hasAccess = (): boolean => {
  return (meData()?.organization_memberships ?? []).length > 0
}

export const organizationSlug = (): string | undefined => {
  return currentOrganization()?.organization_slug
}

const organizationMemberships = (): OrganizationMembership[] => {
  return meData()?.organization_memberships ?? []
}

const defaultUserContext: UserContextType = {
  currentUser,
  setCurrentUser,
  hasAccess,
  organizationSlug,
  currentOrganization,
  setCurrentOrganization,
  organizationMemberships,
  meData,
  isLoadingMe
}

const UserContext = createContext<UserContextType>(defaultUserContext)

export const UserProvider = (props: { children: JSX.Element }): JSX.Element => {
  return <UserContext.Provider value={defaultUserContext}>{props.children}</UserContext.Provider>
}

export const useUser = (): UserContextType => {
  return useContext(UserContext)
}
